import * as React from 'react';

import { MoreHorizontal } from 'lucide-react';

import { Popover, PopoverDialog, PopoverTrigger } from '../overlays/Popover';

import { Button, type ButtonProps } from './Button';

/**
 * Props for the Menu component.
 * Extends Button props but overrides the children prop to be required.
 */
export interface LegacyMenuProps extends Omit<
  ButtonProps,
  'children' | 'size' | 'onPress'
> {
  /**
   * Content to be displayed inside the popover menu.
   * This is typically a list of menu items or other interactive elements.
   */
  children: React.ReactNode;

  /**
   * Determines the placement of the menu popover relative to the trigger button.
   * @default 'bottom'
   */
  placement?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * Distance in pixels between the trigger button and the popover.
   * @default 4
   */
  offset?: number;

  /**
   * Additional CSS class name for the trigger button.
   */
  className?: string;

  /**
   * Additional CSS class name for the popover container.
   */
  popoverClassName?: string;

  /**
   * Additional CSS class name for the popover dialog content.
   */
  dialogClassName?: string;
}

/**
 * Menu component that displays a popover menu when clicked.
 *
 * Uses a Button as the trigger and renders children in a Popover dialog.
 * Commonly used for dropdown menus, context menus, and overflow menus.
 *
 * @example
 * ```tsx
 * <Menu>
 *   <div className="p-2">
 *     <button>Menu Item 1</button>
 *     <button>Menu Item 2</button>
 *   </div>
 * </Menu>
 * ```
 *
 * @param props - Menu properties
 * @returns Rendered Menu component with popover
 */
export const LegacyMenu = ({
  className,
  children,
  variant = 'text',
  color,
  placement = 'bottom',
  offset = 4,
  popoverClassName,
  dialogClassName,
  ...props
}: LegacyMenuProps) => (
  <PopoverTrigger>
    <Button
      variant={variant}
      color={color}
      size="icon"
      className={className}
      icon={<MoreHorizontal />}
      {...props}
    />
    <Popover placement={placement} offset={offset} className={popoverClassName}>
      <PopoverDialog className={dialogClassName}>{children}</PopoverDialog>
    </Popover>
  </PopoverTrigger>
);
